import { useLayoutEffect, useState } from 'react'

export type ElementRect = {
  top: number
  left: number
  width: number
  height: number
}

const rectOf = (element: Element): ElementRect => {
  const { top, left, width, height } = element.getBoundingClientRect()
  return { top, left, width, height }
}

const sameRect = (a: ElementRect | null, b: ElementRect) =>
  a !== null &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height

/**
 * Position à l'écran de l'élément, suivie au défilement (tous conteneurs) et au redimensionnement.
 * Mesurée avant l'affichage : mesurée après, une image passait sans zone mise en avant ni voile.
 */
export const useElementRect = (element: Element | null): ElementRect | null => {
  const [rect, setRect] = useState<ElementRect | null>(null)

  useLayoutEffect(() => {
    if (!element) {
      setRect(null)
      return
    }
    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const next = rectOf(element)
        setRect((current) => (sameRect(current, next) ? current : next))
      })
    }
    setRect(rectOf(element))
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    resizeObserver?.observe(element)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      cancelAnimationFrame(frame)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [element])

  return rect
}
